import {
  configApiRef,
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import { StudioApiClient } from '@s2/studio-api-client';
import { selectedProjectId, selectedTenantId } from './projectScope';

/**
 * useStudioApi builds the typed Studio API client (ADR-016/017).
 *
 * Auth C: the browser talks to the Backstage backend identity proxy (plugin id `studio`), NOT
 * to the Control Plane directly. The proxy authenticates the Backstage user, derives the Studio
 * subject server-side, and forwards the request behind the service (proxy) credential. So the
 * browser holds no Control Plane credential and cannot self-assert its identity:
 *   - baseUrl → discoveryApi.getBaseUrl('studio')  (…/api/studio)
 *   - Authorization → the Backstage USER token from identityApi.getCredentials(); the proxy
 *     validates it (httpAuth) and derives the subject. We attach it explicitly because the
 *     default fetch identity-injection yields an empty token for the guest session.
 *   - tenant/project → passed through for scope; the Control Plane enforces it + user RBAC.
 */
export function useStudioApi(): StudioApiClient {
  const config = useApi(configApiRef);
  const discovery = useApi(discoveryApiRef);
  const identity = useApi(identityApiRef);

  // Runtime project switcher: the user's selection (persisted in localStorage) wins over the
  // configured default, so every request targets the chosen project.
  const tenantId =
    selectedTenantId() || (config.getOptionalString('studio.dev.tenantId') ?? '');
  const projectId =
    selectedProjectId() || (config.getOptionalString('studio.dev.projectId') ?? '');

  // The client builds request URLs as `${baseUrl}${path}`; we keep baseUrl empty and let this
  // wrapper prepend the discovered proxy base and attach the Backstage user token.
  const wrappedFetch: typeof fetch = async (input, init) => {
    const base = await discovery.getBaseUrl('studio');
    const { token } = await identity.getCredentials();
    const url = typeof input === 'string' ? `${base}${input}` : input;
    const headers = new Headers(init?.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetch(url as RequestInfo, { ...init, headers });
  };

  return new StudioApiClient({
    baseUrl: '',
    token: '',
    tenantId,
    projectId,
    fetch: wrappedFetch,
  });
}
